import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { StatCard } from "./LogsTab";

export function AccountsReportTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/accounts-report").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <p className="text-sm text-[#4A5C50]">Loading account report…</p>;

  const roleRows = ["admin", "manager", "customer"].map((role) => ({
    role,
    total: data.role_counts[role] || 0,
    active: data.active_by_role_30d[role] || 0,
  }));

  return (
    <div className="space-y-6" data-testid="accounts-report">
      <div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50]">Account generation</p>
        <h2 className="serif text-3xl mt-1">{data.total_users} accounts total</h2>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Total users" value={data.total_users} testId="acc-stat-total" />
        <StatCard label="New · 24h" value={data.growth.last_24h} testId="acc-stat-24h" tone="green" />
        <StatCard label="New · 7d" value={data.growth.last_7d} testId="acc-stat-7d" tone="green" />
        <StatCard label="New · 30d" value={data.growth.last_30d} testId="acc-stat-30d" tone="green" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 lg:col-span-3" data-testid="acc-chart">
          <h3 className="serif text-2xl mb-4">Cumulative signups · 30 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.series_30d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#4A5C50" tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#4A5C50" />
              <Tooltip />
              <Line type="monotone" dataKey="cumulative" stroke="#184A30" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="total" stroke="#D96C4A" strokeWidth={1.8} strokeDasharray="3 3" dot={false} name="new/day" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 lg:col-span-2" data-testid="acc-role-table">
          <h3 className="serif text-2xl mb-4">By role</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
              <tr><th className="pb-2">Role</th><th className="pb-2 text-right">Total</th><th className="pb-2 text-right">Active · 30d</th></tr>
            </thead>
            <tbody>
              {roleRows.map((r) => (
                <tr key={r.role} className="border-t border-[#E5E0D8]">
                  <td className="py-3 capitalize"><span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#184A30]/10 text-[#184A30]">{r.role}</span></td>
                  <td className="py-3 text-right serif text-xl">{r.total}</td>
                  <td className="py-3 text-right serif text-xl text-[#4A5C50]">{r.active}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden" data-testid="acc-recent">
        <div className="p-4 border-b border-[#E5E0D8]">
          <h3 className="serif text-2xl">Most recent signups</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
            <tr><th className="p-4">When</th><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th></tr>
          </thead>
          <tbody>
            {data.recent_signups.map((u) => (
              <tr key={u.email} className="border-t border-[#E5E0D8]">
                <td className="p-4 mono text-xs">{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</td>
                <td className="p-4">{u.full_name}</td>
                <td className="p-4 mono text-xs">{u.email}</td>
                <td className="p-4"><span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#184A30]/10 text-[#184A30]">{u.role}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
